import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, Plus, Pencil, Trash2, X, Pill } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "ativo", label: "Ativo" },
  { value: "rascunho", label: "Rascunho" },
  { value: "arquivado", label: "Arquivado" },
];

const STATUS_BADGE = {
  ativo: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rascunho: "bg-amber-100 text-amber-800 border-amber-200",
  arquivado: "bg-neutral-200 text-neutral-700 border-neutral-300",
};

function Field({ label, value, testId }) {
  return (
    <div>
      <div className="overline text-muted-foreground mb-1">{label}</div>
      <div className="text-sm text-foreground whitespace-pre-wrap" data-testid={testId}>
        {value && String(value).trim() ? value : <span className="text-muted-foreground">—</span>}
      </div>
    </div>
  );
}

const emptyDrug = () => ({
  nome: "",
  classe_terapeutica: "",
  categoria: "",
  doses_referencia_text: "",
  status: "ativo",
  nutrientes_afetados: "",
  alteracoes_exames: "",
  monitoramento: "",
  alertas_interacoes: "",
  marcadores_relacionados_text: "",
  fonte: "",
});

// Split by comma, trim, filter empties
const splitCsv = (s) =>
  (s || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

function DrugFormModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(
    initial
      ? {
          ...emptyDrug(),
          ...initial,
          doses_referencia_text: (initial.doses_referencia || []).join(", "),
          marcadores_relacionados_text: (initial.marcadores_relacionados || []).join(", "),
        }
      : emptyDrug(),
  );
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.nome.trim()) {
      toast.error("Informe o nome do fármaco.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nome: form.nome.trim(),
        classe_terapeutica: (form.classe_terapeutica || "").trim(),
        categoria: (form.categoria || "").trim(),
        doses_referencia: splitCsv(form.doses_referencia_text),
        status: form.status,
        nutrientes_afetados: form.nutrientes_afetados || "",
        alteracoes_exames: form.alteracoes_exames || "",
        monitoramento: form.monitoramento || "",
        alertas_interacoes: form.alertas_interacoes || "",
        marcadores_relacionados: splitCsv(form.marcadores_relacionados_text),
        fonte: (form.fonte || "").trim(),
      };
      if (isEdit) {
        await api.patch(`/admin/drugs/${initial.id}`, payload);
        toast.success("Fármaco atualizado.");
      } else {
        await api.post("/admin/drugs", payload);
        toast.success("Fármaco criado.");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Erro ao salvar.");
    }
    setSaving(false);
  };

  const testidPrefix = isEdit ? "edit-drug" : "create-drug";

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4"
      onClick={onClose}
      data-testid={`${testidPrefix}-modal`}
    >
      <div
        className="bg-card border border-border rounded-sm p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline text-gold mb-1">{isEdit ? "Editar fármaco" : "Novo fármaco"}</div>
            <h3 className="brand-wordmark uppercase text-lg font-bold text-primary">
              {isEdit ? initial.nome : "Adicionar ao bulário"}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-sm">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="overline text-muted-foreground mb-1 block">Nome</label>
              <input
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                placeholder="Ex: Metformina (Glifage® XR)"
                data-testid="drug-nome-input"
              />
            </div>
            <div>
              <label className="overline text-muted-foreground mb-1 block">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                data-testid="drug-status-select"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="overline text-muted-foreground mb-1 block">Classe terapêutica</label>
              <input
                value={form.classe_terapeutica}
                onChange={(e) => setForm({ ...form, classe_terapeutica: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                placeholder="Ex: Biguanida"
                data-testid="drug-classe-input"
              />
            </div>
            <div>
              <label className="overline text-muted-foreground mb-1 block">Categoria</label>
              <input
                value={form.categoria}
                onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                placeholder="Ex: Antidiabéticos"
                data-testid="drug-categoria-input"
              />
            </div>
          </div>

          <div>
            <label className="overline text-muted-foreground mb-1 block">
              Doses de referência (separadas por vírgula)
            </label>
            <input
              value={form.doses_referencia_text}
              onChange={(e) => setForm({ ...form, doses_referencia_text: e.target.value })}
              className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
              placeholder="Ex: 500mg, 750mg, 1000mg"
              data-testid="drug-doses-input"
            />
          </div>

          <div>
            <label className="overline text-muted-foreground mb-1 block">Nutrientes afetados</label>
            <textarea
              value={form.nutrientes_afetados}
              onChange={(e) => setForm({ ...form, nutrientes_afetados: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
              data-testid="drug-nutrientes-input"
            />
          </div>

          <div>
            <label className="overline text-muted-foreground mb-1 block">
              Exames que o fármaco altera diretamente
            </label>
            <textarea
              value={form.alteracoes_exames}
              onChange={(e) => setForm({ ...form, alteracoes_exames: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
              data-testid="drug-alteracoes-input"
            />
          </div>

          <div>
            <label className="overline text-muted-foreground mb-1 block">Monitoramento recomendado</label>
            <textarea
              value={form.monitoramento}
              onChange={(e) => setForm({ ...form, monitoramento: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
              data-testid="drug-monitoramento-input"
            />
          </div>

          <div>
            <label className="overline text-muted-foreground mb-1 block">
              Sinais de alerta / interações relevantes
            </label>
            <textarea
              value={form.alertas_interacoes}
              onChange={(e) => setForm({ ...form, alertas_interacoes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
              data-testid="drug-alertas-input"
            />
          </div>

          <div>
            <label className="overline text-muted-foreground mb-1 block">
              Marcadores relacionados (nomes separados por vírgula)
            </label>
            <input
              value={form.marcadores_relacionados_text}
              onChange={(e) => setForm({ ...form, marcadores_relacionados_text: e.target.value })}
              className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
              placeholder="Ex: B12 sérica, Homocisteína, Creatinina"
              data-testid="drug-marcadores-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Este campo será usado em uma etapa futura para vincular à Biblioteca de Marcadores.
            </p>
          </div>

          <div>
            <label className="overline text-muted-foreground mb-1 block">Fonte</label>
            <input
              value={form.fonte}
              onChange={(e) => setForm({ ...form, fonte: e.target.value })}
              className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
              placeholder="Ex: Bula ANVISA…"
              data-testid="drug-fonte-input"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted"
            data-testid={`${testidPrefix}-cancel-btn`}
          >
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60"
            data-testid={`${testidPrefix}-save-btn`}
          >
            {saving ? "Salvando…" : isEdit ? "Salvar alterações" : "Criar fármaco"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DrugCard({ d, onEdit, onDelete }) {
  const statusLabel = STATUS_OPTIONS.find((s) => s.value === d.status)?.label || d.status;
  const badgeCls = STATUS_BADGE[d.status] || STATUS_BADGE.arquivado;

  return (
    <div className="bg-card border border-border rounded-sm p-5" data-testid={`drug-card-${d.id}`}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="overline text-gold tracking-[0.22em]" data-testid={`drug-codigo-${d.id}`}>
              {d.codigo}
            </span>
            <span
              className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm border ${badgeCls}`}
              data-testid={`drug-status-${d.id}`}
            >
              {statusLabel}
            </span>
          </div>
          <h3
            className="brand-wordmark uppercase text-lg font-bold text-primary leading-tight"
            data-testid={`drug-nome-${d.id}`}
          >
            {d.nome}
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            {[d.classe_terapeutica, d.categoria].filter(Boolean).join(" · ") || "—"}
          </p>
        </div>
        <div className="flex gap-1 shrink-0">
          <button
            onClick={() => onEdit(d)}
            className="p-1.5 rounded-sm border border-primary/30 text-primary hover:bg-primary/5 transition-colors"
            title="Editar"
            data-testid={`edit-drug-${d.id}`}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onDelete(d)}
            className="p-1.5 rounded-sm border border-destructive/30 text-destructive hover:bg-destructive/5 transition-colors"
            title="Excluir"
            data-testid={`delete-drug-${d.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field
          label="Doses de referência"
          value={(d.doses_referencia || []).join(", ")}
          testId={`drug-doses-${d.id}`}
        />
        <Field label="Fonte" value={d.fonte} testId={`drug-fonte-${d.id}`} />
        <div className="md:col-span-2">
          <Field
            label="Nutrientes afetados"
            value={d.nutrientes_afetados}
            testId={`drug-nutrientes-${d.id}`}
          />
        </div>
        <div className="md:col-span-2">
          <Field
            label="Exames que o fármaco altera diretamente"
            value={d.alteracoes_exames}
            testId={`drug-alteracoes-${d.id}`}
          />
        </div>
        <div className="md:col-span-2">
          <Field
            label="Monitoramento recomendado"
            value={d.monitoramento}
            testId={`drug-monitoramento-${d.id}`}
          />
        </div>
        <div className="md:col-span-2">
          <Field
            label="Sinais de alerta / interações relevantes"
            value={d.alertas_interacoes}
            testId={`drug-alertas-${d.id}`}
          />
        </div>
        <div className="md:col-span-2">
          <div className="overline text-muted-foreground mb-1">Marcadores relacionados</div>
          {(d.marcadores_relacionados || []).length ? (
            <div className="flex flex-wrap gap-1.5" data-testid={`drug-marcadores-${d.id}`}>
              {d.marcadores_relacionados.map((m) => (
                <span
                  key={m}
                  className="text-xs px-2 py-0.5 rounded-sm border border-border bg-muted/50 text-foreground"
                >
                  {m}
                </span>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">—</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminDrugsPage() {
  const [drugs, setDrugs] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/drugs");
      setDrugs(data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Falha ao carregar dados.");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return drugs;
    return drugs.filter((d) => (d.nome || "").toLowerCase().includes(q));
  }, [drugs, search]);

  const remove = async (d) => {
    if (!window.confirm(`Excluir o fármaco "${d.nome}"? Esta ação é permanente.`)) return;
    try {
      await api.delete(`/admin/drugs/${d.id}`);
      toast.success("Fármaco excluído.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Erro ao excluir.");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-drugs-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-gold mb-3 inline-flex items-center gap-2">
            <Pill className="w-3.5 h-3.5" /> Biblioteca
          </div>
          <h1 className="brand-wordmark text-3xl md:text-4xl font-bold tracking-tight uppercase text-primary">
            Bulário de Fármacos
          </h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Cadastro estruturado de medicamentos com nutrientes afetados, exames alterados, monitoramento e sinais de alerta.
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"
          data-testid="new-drug-btn"
        >
          <Plus className="w-4 h-4" /> Novo Fármaco
        </button>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar fármaco por nome…"
          className="w-full pl-10 pr-3 py-2.5 rounded-sm border border-input bg-card text-sm"
          data-testid="drugs-search"
        />
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-muted-foreground overline">Carregando…</div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground" data-testid="drugs-empty">
          Nenhum fármaco encontrado.
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((d) => (
            <DrugCard key={d.id} d={d} onEdit={setEditing} onDelete={remove} />
          ))}
        </div>
      )}

      {creating && (
        <DrugFormModal
          initial={null}
          onClose={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false);
            await load();
          }}
        />
      )}
      {editing && (
        <DrugFormModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
}
